import {LoaderFunctionArgs, RouteObject, RouterContextProvider, redirect} from 'react-router';

import {EPermission, ERoles} from 'src/containers/User';
import PATHS from 'src/paths';
import {getState} from 'src/routerContext';

import {ECorrectionsTab, EMigrationTab, TABS} from './definitions';

const checkAccess = async (tab: ECorrectionsTab, context: RouterContextProvider) => {
  const permissions = getState(context).user?.permissions ?? [];
  const roles = getState(context).user?.roles ?? [];

  switch (tab) {
    case ECorrectionsTab.PAYMENTS:
      return (
        (permissions.includes(EPermission.PERMISSION_TENANT_TRANSACT_STORNED) &&
          roles.includes(ERoles.ROLE_TENANT_TRANSACT_STORNED)) ||
        (permissions.includes(EPermission.PERMISSION_PAYKEEPER_CHANGE) &&
          roles.includes(ERoles.ROLE_PAYKEEPER_CHANGE))
      );
    case ECorrectionsTab.LICENSES:
      return (
        permissions.includes(EPermission.PERMISSION_TENANT_LICENSE_RENEW) &&
        roles.includes(ERoles.ROLE_TENANT_LICENSE_RENEW)
      );
    case ECorrectionsTab.TARIFFS:
      return (
        roles.includes(ERoles.ROLE_SUPPORT) &&
        permissions.includes(EPermission.PERMISSION_TENANT_TARIFF_WRITE)
      );
    case ECorrectionsTab.SMS_CHANNEL:
    case ECorrectionsTab.MIGRATION:
    default:
      return true;
  }
};

const getMainTab = async (context: RouterContextProvider) => {
  for (const tab of TABS) {
    const hasAccess = await checkAccess(tab, context);

    if (hasAccess) {
      return tab;
    }
  }

  return null;
};

const mainTabLoader = async ({context}: LoaderFunctionArgs) => {
  const mainTab = await getMainTab(context);

  return mainTab ? redirect(mainTab) : redirect(PATHS.HOME);
};

const loader =
  (tab: ECorrectionsTab) =>
  async ({context}: LoaderFunctionArgs) => {
    const hasAccess = await checkAccess(tab, context);

    if (!hasAccess) {
      const mainTab = await getMainTab(context);

      return mainTab ? redirect(`../${mainTab}`) : redirect(PATHS.HOME);
    }
  };

const children: RouteObject[] = [
  {
    index: true,
    loader: mainTabLoader,
  },
  {
    path: ECorrectionsTab.PAYMENTS,
    loader: loader(ECorrectionsTab.PAYMENTS),
    lazy: async () => ({
      Component: (await import('./PaymentsTab')).PaymentsTab,
    }),
  },
  {
    path: ECorrectionsTab.LICENSES,
    loader: loader(ECorrectionsTab.LICENSES),
    lazy: async () => ({
      Component: (await import('./LicensesTab')).LicensesTab,
    }),
  },
  {
    path: ECorrectionsTab.SMS_CHANNEL,
    loader: loader(ECorrectionsTab.SMS_CHANNEL),
    lazy: async () => ({
      Component: (await import('./SmsChannelTab')).SmsChannelTab,
    }),
  },
  {
    path: ECorrectionsTab.MIGRATION,
    loader: async (args) => {
      await loader(ECorrectionsTab.MIGRATION)(args);

      return {
        tabs: Object.values(EMigrationTab),
      };
    },
    lazy: async () => ({
      Component: (await import('./MigrationTab')).MigrationTab,
    }),
    children: [
      {
        index: true,
        loader: () => redirect(EMigrationTab.SUBJECTS),
      },
      {
        path: EMigrationTab.SUBJECTS,
        lazy: async () => ({
          Component: (await import('./MigrationTab/SubjectsActions')).SubjectsActions,
        }),
      },
      {
        path: EMigrationTab.PRISM,
        lazy: async () => ({
          Component: (await import('./MigrationTab/PrismActions')).PrismActions,
        }),
      },
    ],
  },
  {
    path: ECorrectionsTab.TARIFFS,
    loader: loader(ECorrectionsTab.TARIFFS),
    lazy: async () => ({
      Component: (await import('./TariffTab')).TariffTab,
    }),
  },
];

export const correctionsRoutes: RouteObject = {
  loader: async ({context}) => {
    const availableTabs: ECorrectionsTab[] = [];

    for (const tab of TABS) {
      const hasAccess = await checkAccess(tab, context);

      if (hasAccess) {
        availableTabs.push(tab);
      }
    }

    return {
      tabs: availableTabs,
    };
  },
  children,
};
